<template>
  <div class="tablet-root">

    <!-- ══════════════════════════════════════════════════
         STICKY HEADER (glassmorphism)
    ═══════════════════════════════════════════════════ -->
    <header class="t-header">
      <div class="t-header-inner">
        <!-- Brand -->
        <div class="t-brand">
          <div class="t-brand-dot"></div>
          <span class="t-brand-name">Mocero One Platform</span>
          <span class="t-brand-sep">·</span>
          <span class="t-brand-org">{{ authStore.user?.organizationName || 'Hospital' }}</span>
        </div>

        <!-- Right -->
        <div class="t-header-right">
          <span class="t-username">{{ authStore.user?.name }}</span>
          <button class="t-bell-btn" title="Notifications">
            <i class="pi pi-bell"></i>
            <span v-if="unreadCount > 0" class="t-bell-badge">{{ unreadCount }}</span>
          </button>
          <button class="t-logout-btn" title="Logout" @click="handleLogout">
            <i class="pi pi-sign-out"></i>
            <span class="t-logout-label">Logout</span>
          </button>
        </div>
      </div>
    </header>

    <!-- ══════════════════════════════════════════════════
         TODAY'S PULSE STRIP
    ═══════════════════════════════════════════════════ -->
    <div class="t-pulse-strip">
      <span class="t-pulse-label">Today's Pulse</span>
      <div class="t-pulse-chips">
        <button class="t-chip t-chip--birthday" @click="scrollTo('t-tasks')">
          <span>🎂</span>
          <span class="t-chip-text">Birthdays</span>
          <span class="t-chip-count">{{ birthdays.length || '—' }}</span>
        </button>
        <button class="t-chip t-chip--tasks" @click="scrollTo('t-tasks')">
          <i class="pi pi-clock"></i>
          <span class="t-chip-text">Due Today</span>
          <span class="t-chip-count">{{ tasksDueToday.length }}</span>
        </button>
        <button class="t-chip t-chip--circulars" @click="scrollTo('t-circulars')">
          <i class="pi pi-file-edit"></i>
          <span class="t-chip-text">Circulars</span>
          <span class="t-chip-count">{{ orgCirculars.length + deptCirculars.length }}</span>
        </button>
        <button class="t-chip t-chip--news" @click="scrollTo('t-news')">
          <i class="pi pi-megaphone"></i>
          <span class="t-chip-text">News & Events</span>
          <span class="t-chip-count">{{ news.length }}</span>
        </button>
        <button class="t-chip t-chip--sop" @click="scrollTo('t-sop')">
          <i class="pi pi-book"></i>
          <span class="t-chip-text">SOPs</span>
          <span class="t-chip-count">{{ sopCount }}</span>
        </button>
        <button class="t-chip t-chip--apps" @click="scrollTo('t-apps')">
          <i class="pi pi-th-large"></i>
          <span class="t-chip-text">Apps</span>
          <span class="t-chip-count">{{ accessibleModules.length }}</span>
        </button>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════
         MAIN BODY — 2-column grid (60 / 40)
    ═══════════════════════════════════════════════════ -->
    <div class="t-main-body">

      <!-- ── LEFT COLUMN (60%) ──────────────────────── -->
      <div class="t-left-col">

        <!-- ─── A. TASKS SECTION ─── -->
        <section id="t-tasks" class="t-card">
          <!-- Header row -->
          <div class="t-card-header">
            <div class="t-card-title-group">
              <i class="pi pi-list-check t-icon-teal"></i>
              <h2 class="t-card-title">Task Dashboard</h2>
            </div>
            <div class="t-task-tabs">
              <button
                class="t-tab-btn"
                :class="{ active: activeTaskTab === 'my' }"
                @click="switchTaskTab('my')"
              >My Tasks</button>
              <button
                class="t-tab-btn"
                :class="{ active: activeTaskTab === 'dept' }"
                @click="switchTaskTab('dept')"
              >Dept Tasks</button>
            </div>
            <button class="t-view-all" @click="router.push('/Task')">
              View All <i class="pi pi-arrow-right"></i>
            </button>
          </div>

          <!-- Stat Row -->
          <div class="t-stat-row">
            <div class="t-stat-card t-stat--pending">
              <span class="t-stat-num">{{ taskStats.pending }}</span>
              <span class="t-stat-lbl">Pending</span>
            </div>
            <div class="t-stat-card t-stat--inprogress">
              <span class="t-stat-num">{{ taskStats.inProgress }}</span>
              <span class="t-stat-lbl">In Progress</span>
            </div>
            <div class="t-stat-card t-stat--overdue">
              <span class="t-stat-num">{{ taskStats.overdue }}</span>
              <span class="t-stat-lbl">Overdue</span>
            </div>
            <div class="t-stat-card t-stat--total">
              <span class="t-stat-num">{{ taskStats.total }}</span>
              <span class="t-stat-lbl">Total</span>
            </div>
          </div>

          <!-- Task Cards List -->
          <div v-if="tasksLoading" class="t-loading-state">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading tasks…</span>
          </div>
          <div v-else-if="sortedTasks.length === 0" class="t-empty-state">
            <i class="pi pi-check-circle"></i>
            <span>No tasks found</span>
          </div>
          <div v-else class="t-task-list">
            <div
              v-for="task in sortedTasks.slice(0, 15)"
              :key="task.TaskId || task.Id"
              class="t-task-card"
              :class="{ 't-task-card--overdue': isPastDue(task.EndDate) && !isTaskCompleted(task) }"
              @click="openTaskPane(task)"
            >
              <span class="t-priority-badge" :class="priorityClass(task.TaskPriorityName)">
                {{ task.TaskPriorityName || 'Normal' }}
              </span>
              <div class="t-task-info">
                <p class="t-task-name">{{ task.TaskName }}</p>
                <p class="t-task-dept">{{ task.DepartmentName }}</p>
              </div>
              <div class="t-task-right">
                <span class="t-status-badge" :class="statusClass(task.TaskStatusName)">
                  {{ task.TaskStatusName }}
                </span>
                <span class="t-due-chip" :class="dueCellClass(task.EndDate, task.TaskStatusName)">
                  {{ timeRemaining(task.EndDate) }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- ─── B. SOP SECTION ─── -->
        <section id="t-sop" class="t-card">
          <div class="t-card-header">
            <div class="t-card-title-group">
              <i class="pi pi-book t-icon-orange"></i>
              <h2 class="t-card-title">SOP Documents</h2>
            </div>
            <button class="t-view-all" @click="router.push('/sop-documents')">
              View All <i class="pi pi-arrow-right"></i>
            </button>
          </div>

          <!-- Search + Category pills -->
          <div class="t-sop-controls">
            <div class="t-sop-search-wrap">
              <i class="pi pi-search t-sop-search-icon"></i>
              <input
                v-model="sopSearchTerm"
                class="t-sop-search"
                type="text"
                placeholder="Search SOPs…"
                @input="onSOPSearch"
              />
              <button v-if="sopSearchTerm" class="t-sop-clear" @click="clearSOPSearch">
                <i class="pi pi-times"></i>
              </button>
            </div>
            <div class="t-cat-pills">
              <button
                v-for="cat in sopCategories"
                :key="cat"
                class="t-cat-pill"
                :class="{ active: selectedCategory === cat }"
                @click="selectCategory(cat)"
              >{{ cat }}</button>
            </div>
          </div>

          <div v-if="sopLoading" class="t-loading-state">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading…</span>
          </div>
          <div v-else-if="filteredSOPs.length === 0" class="t-empty-state">
            <i class="pi pi-inbox"></i>
            <span>No documents found</span>
          </div>
          <ul v-else class="t-sop-list">
            <li
              v-for="(sop, i) in filteredSOPs.slice(0, 10)"
              :key="sop.Id || i"
              class="t-sop-item"
            >
              <div class="t-sop-icon-wrap">
                <i class="pi pi-file-pdf t-sop-file-icon"></i>
              </div>
              <div class="t-sop-details">
                <p class="t-sop-title">{{ sop.Title }}</p>
                <div class="t-sop-meta">
                  <span class="t-sop-cat">{{ sop.Category }}</span>
                  <span v-if="sop.Version" class="t-sop-version">v{{ sop.Version.replace(/^v/i, '') }}</span>
                </div>
              </div>
              <a
                v-if="sop.FullDocumentPath || sop.FilePath"
                :href="sop.FullDocumentPath || sop.FilePath"
                target="_blank"
                class="t-sop-download"
                title="Download"
                @click.stop
              >
                <i class="pi pi-download"></i>
              </a>
            </li>
          </ul>
        </section>
      </div>

      <!-- ── RIGHT COLUMN (40%) ─────────────────────── -->
      <div class="t-right-col">

        <!-- ─── A. CIRCULARS SECTION ─── -->
        <section id="t-circulars" class="t-card">
          <div class="t-card-header">
            <div class="t-card-title-group">
              <i class="pi pi-file-edit t-icon-blue"></i>
              <h2 class="t-card-title">Circulars</h2>
            </div>
            <!-- Admin dept filter inline (tablet has space) -->
            <MOPDropdown
              v-if="isAdmin"
              v-model="selectedDeptId"
              :options="deptOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter Dept"
              :showClear="true"
              class="t-dept-filter"
            />
            <button class="t-view-all" @click="router.push('/circulars')">
              View All <i class="pi pi-arrow-right"></i>
            </button>
          </div>

          <!-- Org Circulars -->
          <div class="t-circular-group t-circular-group--org">
            <div class="t-circular-group-header">
              <span class="t-circular-dot t-circular-dot--org"></span>
              <span class="t-circular-group-title">Organisation</span>
              <span class="t-circular-count t-circular-count--org">{{ orgCirculars.length }}</span>
            </div>
            <div v-if="orgCirculars.length === 0" class="t-empty-state t-empty-state--sm">
              <i class="pi pi-inbox"></i>
              <span>No org circulars</span>
            </div>
            <ul v-else class="t-circular-list">
              <li
                v-for="(c, i) in orgCirculars.slice(0, 5)"
                :key="c.Id || i"
                class="t-circular-item"
                @click="openCircularPane(c, 'org')"
              >
                <div class="t-circular-info">
                  <p class="t-circular-title">{{ c.Title }}</p>
                  <p class="t-circular-date">{{ formatDate(c.PublishedDate || c.CreatedDate) }}</p>
                </div>
                <a
                  v-if="c.FullAttachmentPath"
                  :href="c.FullAttachmentPath"
                  target="_blank"
                  class="t-circular-attach"
                  title="Download"
                  @click.stop
                >
                  <i class="pi pi-paperclip"></i>
                </a>
              </li>
            </ul>
          </div>

          <!-- Dept Circulars -->
          <div class="t-circular-group t-circular-group--dept">
            <div class="t-circular-group-header">
              <span class="t-circular-dot t-circular-dot--dept"></span>
              <span class="t-circular-group-title">Department</span>
              <span class="t-circular-count t-circular-count--dept">{{ deptCirculars.length }}</span>
            </div>
            <div v-if="deptCirculars.length === 0" class="t-empty-state t-empty-state--sm">
              <i class="pi pi-inbox"></i>
              <span>No dept circulars</span>
            </div>
            <ul v-else class="t-circular-list">
              <li
                v-for="(c, i) in deptCirculars.slice(0, 5)"
                :key="c.Id || i"
                class="t-circular-item"
                @click="openCircularPane(c, 'dept')"
              >
                <div class="t-circular-info">
                  <p class="t-circular-title">{{ c.Title }}</p>
                  <p class="t-circular-date">
                    {{ formatDate(c.PublishedDate || c.CreatedDate) }}
                    <span v-if="c.DepartmentName" class="t-circular-dept-tag">{{ c.DepartmentName }}</span>
                  </p>
                </div>
                <a
                  v-if="c.FullAttachmentPath"
                  :href="c.FullAttachmentPath"
                  target="_blank"
                  class="t-circular-attach"
                  title="Download"
                  @click.stop
                >
                  <i class="pi pi-paperclip"></i>
                </a>
              </li>
            </ul>
          </div>
        </section>

        <!-- ─── B. NEWS & EVENTS SECTION ─── -->
        <section id="t-news" class="t-card">
          <div class="t-card-header">
            <div class="t-card-title-group">
              <i class="pi pi-megaphone t-icon-amber"></i>
              <h2 class="t-card-title">News & Events</h2>
            </div>
            <button class="t-view-all" @click="router.push('/news-events')">
              View All <i class="pi pi-arrow-right"></i>
            </button>
          </div>

          <!-- Category pills -->
          <div class="t-news-pills">
            <button
              v-for="cat in newsCategories"
              :key="cat"
              class="t-cat-pill"
              :class="{ active: selectedNewsCategory === cat }"
              @click="selectNewsCategory(cat)"
            >{{ cat }}</button>
          </div>

          <div v-if="news.length === 0" class="t-empty-state">
            <i class="pi pi-bell"></i>
            <span>No news available</span>
          </div>
          <div v-else class="t-news-list">
            <div
              v-for="(item, i) in news.slice(0, 8)"
              :key="item.Id || i"
              class="t-news-card"
              @click="openNewsPane(item)"
            >
              <div class="t-news-badges">
                <span class="t-news-type" :class="item.Type == 1 ? 't-badge-news' : 't-badge-event'">
                  {{ item.Type == 1 ? 'News' : 'Event' }}
                </span>
                <span v-if="item.Category && item.Category !== 'General'" class="t-news-cat">
                  {{ item.Category }}
                </span>
              </div>
              <p class="t-news-title">{{ item.Title }}</p>
              <p class="t-news-date">{{ formatDate(item.EventDate) }}</p>
              <p class="t-news-desc">{{ item.Description }}</p>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════
         APPS SECTION — full width below grid
    ═══════════════════════════════════════════════════ -->
    <section id="t-apps" class="t-apps-section">
      <div class="t-apps-header-row">
        <div class="t-card-title-group">
          <i class="pi pi-th-large t-icon-teal"></i>
          <h2 class="t-card-title">Applications</h2>
        </div>
      </div>

      <div v-if="loading" class="t-apps-skeleton">
        <div v-for="n in 8" :key="n" class="t-app-skeleton-tile"></div>
      </div>
      <div v-else-if="error" class="t-empty-state">
        <i class="pi pi-exclamation-circle"></i>
        <span>{{ error }}</span>
        <button class="t-retry-btn" @click="loadModules">
          <i class="pi pi-refresh"></i> Retry
        </button>
      </div>
      <template v-else>
        <div v-if="accessibleModules.length" class="t-apps-grid">
          <div
            v-for="mod in accessibleModules"
            :key="mod.id"
            class="t-app-tile"
            @click="handleModuleSelect(mod)"
          >
            <div class="t-app-icon" :style="{ background: `${mod.color || '#0d9488'}22` }">
              <img :src="mod.icon" :alt="mod.name" class="t-app-img" />
            </div>
            <span class="t-app-name">{{ mod.name }}</span>
          </div>
        </div>
        <div v-if="toBePurchasedModules.length" class="mt-4">
          <p class="t-apps-section-label">Other Modules</p>
          <div class="t-apps-grid t-apps-grid--locked">
            <div
              v-for="mod in toBePurchasedModules"
              :key="mod.id"
              class="t-app-tile t-app-tile--locked"
            >
              <div class="t-app-icon" :style="{ background: `${mod.color || '#94a3b8'}22` }">
                <img :src="mod.icon" :alt="mod.name" class="t-app-img" />
              </div>
              <span class="t-app-name">{{ mod.name }}</span>
              <i class="pi pi-lock t-app-lock-icon"></i>
            </div>
          </div>
        </div>
        <div v-if="!accessibleModules.length && !toBePurchasedModules.length" class="t-empty-state">
          <i class="pi pi-box"></i>
          <span>No applications found</span>
        </div>
      </template>
    </section>

    <!-- ══════════════════════════════════════════════════
         TASK DETAIL DRAWER (right pane)
    ═══════════════════════════════════════════════════ -->
    <MOPDrawer
      v-model:visible="showTaskPane"
      position="right"
      :style="{ width: '420px' }"
      :modal="true"
      :dismissableMask="true"
      header="Task Details"
    >
      <div v-if="selectedTask" class="t-drawer-body">
        <h2 class="t-drawer-task-name">{{ selectedTask.TaskName }}</h2>

        <div class="t-drawer-badges">
          <span class="t-priority-badge" :class="priorityClass(selectedTask.TaskPriorityName)">
            {{ selectedTask.TaskPriorityName || 'Normal' }}
          </span>
          <span class="t-status-badge" :class="statusClass(selectedTask.TaskStatusName)">
            {{ selectedTask.TaskStatusName }}
          </span>
        </div>

        <div class="t-drawer-meta-grid">
          <div class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Department</span>
            <span class="t-drawer-meta-value">{{ selectedTask.DepartmentName || '—' }}</span>
          </div>
          <div class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Assignee</span>
            <span class="t-drawer-meta-value">{{ selectedTask.AssigneeName || selectedTask.AssignedToName || '—' }}</span>
          </div>
          <div class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Due Date</span>
            <span class="t-drawer-meta-value">{{ formatDate(selectedTask.EndDate) }}</span>
          </div>
          <div class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Time Remaining</span>
            <span
              class="t-drawer-meta-value"
              :class="dueCellClass(selectedTask.EndDate, selectedTask.TaskStatusName)"
            >{{ timeRemaining(selectedTask.EndDate) }}</span>
          </div>
          <div v-if="selectedTask.StartDate" class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Start Date</span>
            <span class="t-drawer-meta-value">{{ formatDate(selectedTask.StartDate) }}</span>
          </div>
          <div v-if="selectedTask.CreatedBy || selectedTask.CreatedByName" class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Created By</span>
            <span class="t-drawer-meta-value">{{ selectedTask.CreatedByName || selectedTask.CreatedBy }}</span>
          </div>
        </div>

        <div v-if="selectedTask.Description || selectedTask.TaskDescription" class="t-drawer-section">
          <p class="t-drawer-section-title">Description</p>
          <p class="t-drawer-description">{{ selectedTask.Description || selectedTask.TaskDescription }}</p>
        </div>

        <button class="t-drawer-open-btn" @click="router.push('/Task')">
          <i class="pi pi-external-link"></i>
          Open Full Details
        </button>
      </div>
    </MOPDrawer>

    <!-- ══════════════════════════════════════════════════
         CIRCULAR DETAIL DRAWER (right pane)
    ═══════════════════════════════════════════════════ -->
    <MOPDrawer
      v-model:visible="showCircularPane"
      position="right"
      :style="{ width: '420px' }"
      :modal="true"
      :dismissableMask="true"
      header="Circular Details"
    >
      <div v-if="selectedCircular" class="t-drawer-body">
        <div class="t-drawer-circular-type-badge" :class="selectedCircular._type === 'org' ? 't-circ-org' : 't-circ-dept'">
          {{ selectedCircular._type === 'org' ? 'Organisation' : 'Department' }}
        </div>
        <h2 class="t-drawer-task-name">{{ selectedCircular.Title }}</h2>

        <div class="t-drawer-meta-grid">
          <div v-if="selectedCircular.DepartmentName" class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Department</span>
            <span class="t-drawer-meta-value">{{ selectedCircular.DepartmentName }}</span>
          </div>
          <div class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Type</span>
            <span class="t-drawer-meta-value">{{ selectedCircular.TypeName || selectedCircular.Type || '—' }}</span>
          </div>
          <div class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Date</span>
            <span class="t-drawer-meta-value">{{ formatDate(selectedCircular.PublishedDate || selectedCircular.CreatedDate) }}</span>
          </div>
        </div>

        <div v-if="selectedCircular.Content || selectedCircular.Description" class="t-drawer-section">
          <p class="t-drawer-section-title">Content</p>
          <p class="t-drawer-description">{{ selectedCircular.Content || selectedCircular.Description }}</p>
        </div>

        <div
          v-if="selectedCircular.ContentHtml"
          class="t-drawer-section t-drawer-html-content"
          v-html="selectedCircular.ContentHtml"
        ></div>

        <a
          v-if="selectedCircular.FullAttachmentPath"
          :href="selectedCircular.FullAttachmentPath"
          target="_blank"
          class="t-drawer-open-btn t-drawer-open-btn--secondary"
        >
          <i class="pi pi-download"></i>
          Download Attachment
        </a>
      </div>
    </MOPDrawer>

    <!-- ══════════════════════════════════════════════════
         NEWS DETAIL DRAWER (right pane)
    ═══════════════════════════════════════════════════ -->
    <MOPDrawer
      v-model:visible="showNewsPane"
      position="right"
      :style="{ width: '420px' }"
      :modal="true"
      :dismissableMask="true"
      header="News & Events"
    >
      <div v-if="selectedNews" class="t-drawer-body">
        <div class="t-drawer-badges">
          <span class="t-news-type" :class="selectedNews.Type == 1 ? 't-badge-news' : 't-badge-event'">
            {{ selectedNews.Type == 1 ? 'News' : 'Event' }}
          </span>
          <span v-if="selectedNews.Category" class="t-news-cat">{{ selectedNews.Category }}</span>
        </div>
        <h2 class="t-drawer-task-name">{{ selectedNews.Title }}</h2>

        <div class="t-drawer-meta-grid">
          <div class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Date</span>
            <span class="t-drawer-meta-value">{{ formatDate(selectedNews.EventDate) }}</span>
          </div>
          <div v-if="selectedNews.AuthorName" class="t-drawer-meta-item">
            <span class="t-drawer-meta-label">Author</span>
            <span class="t-drawer-meta-value">{{ selectedNews.AuthorName }}</span>
          </div>
        </div>

        <div v-if="selectedNews.Description" class="t-drawer-section">
          <p class="t-drawer-section-title">Description</p>
          <p class="t-drawer-description">{{ selectedNews.Description }}</p>
        </div>

        <div
          v-if="selectedNews.ContentHtml || selectedNews.Content"
          class="t-drawer-section t-drawer-html-content"
          v-html="selectedNews.ContentHtml || selectedNews.Content"
        ></div>
      </div>
    </MOPDrawer>

  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useDashboardData } from './useDashboardData'

const router = useRouter()
const authStore = useAuthStore()

const {
  // State
  loading,
  error,
  modules,
  news,
  selectedNewsCategory,
  newsCategories,
  orgCirculars,
  deptCirculars,
  deptOptions,
  selectedDeptId,
  isAdmin,
  socialPages,
  activeSocialTab,
  birthdays,
  activeTaskTab,
  tasksLoading,
  myTasks,
  deptTasksList,
  currentTasks,
  allSOPs,
  filteredSOPs,
  sopLoading,
  sopSearchTerm,
  selectedCategory,
  sopCategories,
  unreadCount,
  filteredMenus,
  menuarray,
  storeData,

  // Computed
  accessibleModules,
  toBePurchasedModules,
  tasksDueToday,
  sopCount,
  taskStats,
  sortedTasks,
  taskColumns,

  // Functions
  loadModules,
  loadNews,
  selectNewsCategory,
  loadDashboardData,
  loadCirculars,
  loadSocialPages,
  loadBirthdays,
  loadMyTasks,
  loadDeptTasks,
  loadSOPs,
  switchTaskTab,
  onSOPSearch,
  applySOPFilter,
  clearSOPSearch,
  selectCategory,
  registerFCMToken,
  handleModuleSelect,
  handleMoceroUIModule,
  handleMOPUIModule,
  getModuleByName,
  handleLogout,
  LoadMenus,
  LoadStore,
  formatDate,
  isPastDue,
  isTaskCompleted,
  timeRemaining,
  dueCellClass,
  getInitials,
  priorityClass,
  statusClass,
  socialIcon,
  scrollTo
} = useDashboardData()

// ── Right pane state ──────────────────────────────────────
const selectedTask = ref<any>(null)
const showTaskPane = ref(false)

const selectedCircular = ref<any>(null)
const showCircularPane = ref(false)

const selectedNews = ref<any>(null)
const showNewsPane = ref(false)

// ── Pane openers ─────────────────────────────────────────
function openTaskPane(task: any) {
  selectedTask.value = task
  showTaskPane.value = true
}

function openCircularPane(circular: any, type: 'org' | 'dept') {
  selectedCircular.value = { ...circular, _type: type }
  showCircularPane.value = true
}

function openNewsPane(newsItem: any) {
  selectedNews.value = newsItem
  showNewsPane.value = true
}
</script>

<style scoped>
/* ── Root ──────────────────────────────────────────────── */
.tablet-root {
  min-height: 100vh;
  background: #f1f5f9;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ── Header ────────────────────────────────────────────── */
.t-header {
  position: sticky;
  top: 0;
  z-index: 50;
  height: 64px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

.t-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 24px;
}

.t-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.t-brand-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #0d9488;
  flex-shrink: 0;
}

.t-brand-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.2px;
}

.t-brand-sep {
  color: #94a3b8;
  font-size: 14px;
}

.t-brand-org {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.t-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.t-username {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
}

.t-bell-btn {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #374151;
  font-size: 15px;
  transition: background 0.15s;
}

.t-bell-btn:hover {
  background: #e2e8f0;
}

.t-bell-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 17px;
  height: 17px;
  border-radius: 9px;
  background: #dc2626;
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
}

.t-logout-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
}

.t-logout-btn:hover {
  background: #fee2e2;
  color: #dc2626;
  border-color: #fecaca;
}

/* ── Pulse Strip ───────────────────────────────────────── */
.t-pulse-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
  scrollbar-width: none;
}

.t-pulse-strip::-webkit-scrollbar {
  display: none;
}

.t-pulse-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  white-space: nowrap;
}

.t-pulse-chips {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.t-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.12s, box-shadow 0.12s;
}

.t-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.t-chip-text { font-size: 13px; }
.t-chip-count {
  font-size: 12px;
  font-weight: 700;
  background: rgba(0,0,0,0.08);
  padding: 1px 6px;
  border-radius: 10px;
}

.t-chip--birthday  { background: #fdf4ff; border-color: #e9d5ff; color: #7c3aed; }
.t-chip--tasks     { background: #fff7ed; border-color: #fed7aa; color: #c2410c; }
.t-chip--circulars { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.t-chip--news      { background: #fffbeb; border-color: #fde68a; color: #b45309; }
.t-chip--sop       { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
.t-chip--apps      { background: #f0fdfa; border-color: #99f6e4; color: #0f766e; }

/* ── Main Body Grid ────────────────────────────────────── */
.t-main-body {
  display: grid;
  grid-template-columns: 60% 40%;
  gap: 16px;
  padding: 20px 24px;
  align-items: start;
}

.t-left-col,
.t-right-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Card ──────────────────────────────────────────────── */
.t-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 16px;
  overflow: hidden;
}

.t-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.t-card-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.t-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.t-icon-teal   { color: #0d9488; font-size: 15px; }
.t-icon-blue   { color: #3b82f6; font-size: 15px; }
.t-icon-amber  { color: #f59e0b; font-size: 15px; }
.t-icon-orange { color: #f97316; font-size: 15px; }

.t-view-all {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #0d9488;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
  transition: background 0.12s;
}

.t-view-all:hover {
  background: #f0fdfa;
}

/* ── Task Tabs ─────────────────────────────────────────── */
.t-task-tabs {
  display: flex;
  gap: 4px;
}

.t-tab-btn {
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}

.t-tab-btn.active {
  background: #0d9488;
  border-color: #0d9488;
  color: white;
}

/* ── Stat Row ──────────────────────────────────────────── */
.t-stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}

.t-stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 6px;
  border-radius: 10px;
  border: 1px solid transparent;
}

.t-stat-num {
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
}

.t-stat-lbl {
  font-size: 11px;
  font-weight: 500;
  margin-top: 4px;
  text-align: center;
}

.t-stat--pending   { background: #f1f5f9; border-color: #e2e8f0; color: #475569; }
.t-stat--inprogress{ background: #dbeafe; border-color: #bfdbfe; color: #2563eb; }
.t-stat--overdue   { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
.t-stat--total     { background: #f0fdfa; border-color: #99f6e4; color: #0f766e; }

/* ── Task Cards ────────────────────────────────────────── */
.t-task-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.t-task-list::-webkit-scrollbar {
  width: 4px;
}

.t-task-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.t-task-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.t-task-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fafafa;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.t-task-card:hover {
  border-color: #0d9488;
  box-shadow: 0 2px 8px rgba(13, 148, 136, 0.1);
}

.t-task-card--overdue {
  border-left: 3px solid #dc2626;
  background: #fff5f5;
}

.t-task-info {
  flex: 1;
  min-width: 0;
}

.t-task-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.t-task-dept {
  font-size: 11px;
  color: #64748b;
  margin: 2px 0 0 0;
}

.t-task-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

/* ── Priority Badges ───────────────────────────────────── */
.t-priority-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  white-space: nowrap;
}

.priority-high   { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.priority-medium { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
.priority-low    { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

/* ── Status Badges ─────────────────────────────────────── */
.t-status-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
}

.status-completed  { background: #dcfce7; color: #16a34a; }
.status-inprogress { background: #dbeafe; color: #2563eb; }
.status-pending    { background: #f1f5f9; color: #64748b; }

/* ── Due Chip ──────────────────────────────────────────── */
.t-due-chip {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
}

.due-overdue   { background: #fef2f2; color: #dc2626; }
.due-soon      { background: #fffbeb; color: #d97706; }
.due-ok        { background: #f0fdf4; color: #16a34a; }
.due-completed { background: #dcfce7; color: #16a34a; }

/* ── SOP Controls ──────────────────────────────────────── */
.t-sop-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
}

.t-sop-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.t-sop-search-icon {
  position: absolute;
  left: 10px;
  color: #94a3b8;
  font-size: 13px;
}

.t-sop-search {
  width: 100%;
  height: 36px;
  padding: 0 32px 0 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 13px;
  color: #0f172a;
  outline: none;
  transition: border-color 0.15s;
}

.t-sop-search:focus {
  border-color: #0d9488;
  background: white;
}

.t-sop-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 12px;
  padding: 2px;
}

.t-sop-clear:hover {
  color: #475569;
}

/* ── Category Pills ────────────────────────────────────── */
.t-cat-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.t-cat-pill {
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.12s;
  white-space: nowrap;
}

.t-cat-pill.active {
  background: #0d9488;
  border-color: #0d9488;
  color: white;
}

.t-cat-pill:hover:not(.active) {
  border-color: #0d9488;
  color: #0d9488;
}

/* ── SOP List ──────────────────────────────────────────── */
.t-sop-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
}

.t-sop-list::-webkit-scrollbar { width: 4px; }
.t-sop-list::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
.t-sop-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

.t-sop-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fafafa;
  transition: border-color 0.12s;
}

.t-sop-item:hover {
  border-color: #f97316;
}

.t-sop-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #fff7ed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.t-sop-file-icon {
  color: #f97316;
  font-size: 15px;
}

.t-sop-details {
  flex: 1;
  min-width: 0;
}

.t-sop-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.t-sop-meta {
  display: flex;
  gap: 6px;
  margin-top: 3px;
}

.t-sop-cat {
  font-size: 11px;
  color: #64748b;
}

.t-sop-version {
  font-size: 11px;
  color: #94a3b8;
}

.t-sop-download {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
  font-size: 13px;
  text-decoration: none;
  flex-shrink: 0;
  transition: background 0.12s;
}

.t-sop-download:hover {
  background: #dcfce7;
}

/* ── Circulars ─────────────────────────────────────────── */
.t-dept-filter {
  font-size: 12px !important;
  max-width: 160px;
}

.t-circular-group {
  margin-bottom: 14px;
}

.t-circular-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.t-circular-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.t-circular-dot--org  { background: #3b82f6; }
.t-circular-dot--dept { background: #22c55e; }

.t-circular-group-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.t-circular-count {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 10px;
}

.t-circular-count--org  { background: #eff6ff; color: #3b82f6; }
.t-circular-count--dept { background: #f0fdf4; color: #22c55e; }

.t-circular-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.t-circular-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.12s;
}

.t-circular-group--org .t-circular-item:hover  { border-color: #3b82f6; }
.t-circular-group--dept .t-circular-item:hover { border-color: #22c55e; }

.t-circular-info {
  flex: 1;
  min-width: 0;
}

.t-circular-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.t-circular-date {
  font-size: 11px;
  color: #64748b;
  margin: 2px 0 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.t-circular-dept-tag {
  font-size: 10px;
  font-weight: 600;
  background: #f0fdf4;
  color: #16a34a;
  padding: 1px 6px;
  border-radius: 8px;
}

.t-circular-attach {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  text-decoration: none;
  flex-shrink: 0;
  transition: background 0.12s;
}

.t-circular-attach:hover {
  background: #e2e8f0;
}

/* ── News ──────────────────────────────────────────────── */
.t-news-pills {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  margin-bottom: 12px;
  padding-bottom: 2px;
}

.t-news-pills::-webkit-scrollbar { display: none; }

.t-news-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 380px;
  overflow-y: auto;
}

.t-news-list::-webkit-scrollbar { width: 4px; }
.t-news-list::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
.t-news-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

.t-news-card {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s;
}

.t-news-card:hover {
  border-color: #f59e0b;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.t-news-badges {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.t-news-type {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  text-transform: uppercase;
}

.t-badge-news  { background: #eff6ff; color: #3b82f6; }
.t-badge-event { background: #f0fdf4; color: #22c55e; }

.t-news-cat {
  font-size: 10px;
  font-weight: 600;
  background: #fffbeb;
  color: #b45309;
  padding: 2px 7px;
  border-radius: 10px;
}

.t-news-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 3px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.t-news-date {
  font-size: 11px;
  color: #94a3b8;
  margin: 0 0 4px 0;
}

.t-news-desc {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Apps Section ──────────────────────────────────────── */
.t-apps-section {
  margin: 0 24px 24px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.t-apps-header-row {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.t-apps-section-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 10px 0;
}

.t-apps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.t-apps-grid--locked {
  opacity: 0.5;
}

.t-app-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 10px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fafafa;
  cursor: pointer;
  position: relative;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.12s;
}

.t-app-tile:hover:not(.t-app-tile--locked) {
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.12);
  border-color: #0d9488;
  transform: translateY(-2px);
}

.t-app-tile--locked {
  cursor: not-allowed;
}

.t-app-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.t-app-img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.t-app-name {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  line-height: 1.3;
}

.t-app-lock-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  color: #94a3b8;
  font-size: 11px;
}

.t-apps-skeleton {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.t-app-skeleton-tile {
  height: 80px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Shared States ─────────────────────────────────────── */
.t-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: #64748b;
  font-size: 13px;
}

.t-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

.t-empty-state i {
  font-size: 22px;
}

.t-empty-state--sm {
  padding: 12px;
  font-size: 12px;
}

.t-empty-state--sm i {
  font-size: 16px;
}

.t-retry-btn {
  margin-top: 8px;
  padding: 6px 14px;
  border-radius: 8px;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  color: #0d9488;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

/* ── Drawer Body ───────────────────────────────────────── */
.t-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
}

.t-drawer-task-name {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
  margin: 0;
}

.t-drawer-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.t-drawer-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.t-drawer-meta-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.t-drawer-meta-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.t-drawer-meta-value {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.t-drawer-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e2e8f0;
}

.t-drawer-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 8px 0;
}

.t-drawer-description {
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.t-drawer-html-content {
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
}

.t-drawer-open-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: #0d9488;
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, transform 0.12s;
}

.t-drawer-open-btn:hover {
  background: #0f766e;
  transform: translateY(-1px);
}

.t-drawer-open-btn--secondary {
  background: #f8fafc;
  color: #0d9488;
  border: 1px solid #99f6e4;
}

.t-drawer-open-btn--secondary:hover {
  background: #f0fdfa;
}

.t-drawer-circular-type-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.t-circ-org  { background: #eff6ff; color: #3b82f6; }
.t-circ-dept { background: #f0fdf4; color: #22c55e; }

/* ── Responsive ────────────────────────────────────────── */
@media (max-width: 900px) {
  .t-main-body {
    grid-template-columns: 1fr;
  }

  .t-apps-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .t-stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .t-header-inner {
    padding: 0 16px;
  }

  .t-main-body {
    padding: 16px;
  }

  .t-apps-section {
    margin: 0 16px 16px;
  }

  .t-logout-label {
    display: none;
  }

  .t-apps-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
